import { Lock } from 'lucide-react'
import type { AccountTone } from '../../lib/mail-account-scope'
import { accountInitials } from '../../lib/mail-account-scope'
import { cn } from '../../lib/utils'

/** Tailwind-Klassen je Firmenfarbe (#535), als Literale für den JIT. */
export const TONE_CLASSES: Record<
  AccountTone,
  { dot: string; soft: string; edge: string; tile: string }
> = {
  accent: {
    dot: 'bg-accent',
    soft: 'bg-accent/15 text-accent',
    edge: 'border-l-accent',
    tile: 'bg-accent/20 text-accent',
  },
  info: {
    dot: 'bg-info',
    soft: 'bg-info/15 text-info',
    edge: 'border-l-info',
    tile: 'bg-info/20 text-info',
  },
  success: {
    dot: 'bg-success',
    soft: 'bg-success/15 text-success',
    edge: 'border-l-success',
    tile: 'bg-success/20 text-success',
  },
}

/** Firmen-Badge: Punkt + Kontoname in der Firmenfarbe (Sammelposteingang). */
export function AccountBadge({
  label,
  tone,
  className,
}: {
  label: string
  tone: AccountTone
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex min-w-0 max-w-[10rem] shrink-0 items-center gap-1 rounded-full px-1.5 py-px text-[10px] font-medium leading-[14px]',
        TONE_CLASSES[tone].soft,
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE_CLASSES[tone].dot)} />
      <span className="truncate">{label}</span>
    </span>
  )
}

/** Kürzel-Kachel in Firmenfarbe („MG" für „Muster GmbH"). */
export function AccountTile({
  label,
  tone,
  size = 'md',
}: {
  label: string
  tone: AccountTone
  size?: 'sm' | 'md'
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center font-bold',
        size === 'sm' ? 'h-5 w-5 rounded-md text-[10px]' : 'h-9 w-9 rounded-[10px] text-xs',
        TONE_CLASSES[tone].tile,
      )}
    >
      {accountInitials(label)}
    </span>
  )
}

/** Drei Firmenpunkte für „Alle Posteingänge". */
export function AccountDots({ tones }: { tones: readonly AccountTone[] }) {
  return (
    <span aria-hidden="true" className="flex items-center -space-x-0.5">
      {tones.slice(0, 3).map((tone, index) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed order, at most three dots
          key={index}
          className={cn('h-2 w-2 rounded-full ring-1 ring-bg-base', TONE_CLASSES[tone].dot)}
        />
      ))}
    </span>
  )
}

/** Schloss für geschützte Konten (Token `warning`, #524). */
export function AccountLock() {
  return (
    <span
      className="inline-flex shrink-0 rounded-md bg-warning/15 p-0.5 text-warning"
      role="img"
      aria-label="Geschützt"
      title="Geschützt"
    >
      <Lock className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
    </span>
  )
}
